import { useSnackbarController, useReferenceDialog } from "@firecms/core";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { httpsCallable } from "firebase/functions";
import { getEventPath } from "../../utils";
import { buildTeamsCollection } from "../../collections/teams";
import { useFirebaseFunctions } from "../../hooks/useFirebaseFunctions";
import { getModeCollectionIds } from "../../workspace/adminMode";
import { useAdminWorkspace } from "../../workspace/useAdminWorkspace";
import { registerOpenChangeTeamDialog } from "./changeSlotTeamBridge";
import { useAdminTranslation } from "../../i18n/useAdminTranslation";

export function ChangeSlotTeamDialogHost() {
  const userIdRef = useRef<string | null>(null);
  const loadingRef = useRef(false);
  const { mode } = useAdminWorkspace();
  const eventId = useMemo(
    () => getModeCollectionIds(mode).event ?? null,
    [mode]
  );
  const functions = useFirebaseFunctions();
  const snackbarController = useSnackbarController();
  const { t: ta } = useAdminTranslation();

  const assignTeam = useCallback(
    async (teamEntity: { id: string } | null) => {
      const currentUserId = userIdRef.current;
      if (!functions || !teamEntity || !eventId || !currentUserId) {
        return;
      }

      const changeEventUserTeam = httpsCallable(functions, "changeEventUserTeam");

      try {
        loadingRef.current = true;
        await changeEventUserTeam({
          accountId: eventId.accountId,
          spaceId: eventId.spaceId,
          eventId: eventId.eventId,
          userId: currentUserId,
          teamId: teamEntity.id,
        });
        snackbarController.open({
          message: ta("changeTeam.teamUpdated"),
          type: "success",
        });
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : ta("common.unknownError");
        snackbarController.open({
          message: ta("changeTeam.error", { message }),
          type: "error",
        });
        console.error(error);
      } finally {
        loadingRef.current = false;
        userIdRef.current = null;
      }
    },
    [eventId, functions, snackbarController, ta]
  );

  const referenceDialog = useReferenceDialog({
    path: eventId ? getEventPath(eventId) + "/teams" : "teams",
    collection: eventId
      ? buildTeamsCollection(eventId)
      : buildTeamsCollection({
          accountId: "",
          spaceId: "",
          eventId: "",
        }),
    multiselect: false,
    onSingleEntitySelected: assignTeam,
  });

  const openReferenceDialogRef = useRef(referenceDialog.open);
  openReferenceDialogRef.current = referenceDialog.open;

  useEffect(() => {
    return registerOpenChangeTeamDialog((id) => {
      if (!eventId) {
        snackbarController.open({
          message: ta("common.selectActiveEvent"),
          type: "warning",
        });
        return;
      }
      if (loadingRef.current) {
        return;
      }
      userIdRef.current = id;
      openReferenceDialogRef.current();
    });
  }, [eventId, snackbarController, ta]);

  return null;
}
